import { useRef } from 'react';

import type { SmartBuildClassroom, SmartBuildPurpose, SmartBuildScopeOption, SmartBuildScopeType } from 'pages/custom_tests/components/custom_test_wizard/context';
import { NumberStepper } from 'pages/custom_tests/components/custom_test_wizard/components/number_stepper/NumberStepper';
import { useCustomTestWizard } from 'pages/custom_tests/components/custom_test_wizard/context';
import { EWizardStep } from 'pages/custom_tests/components/custom_test_wizard/machine';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { Alert, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

// Statement panel gradient (purple fading to navy, per the design file).
const PANEL_GRADIENT = 'linear-gradient(180deg, #5d4a9c 0%, #12263f 65%)';

type PurposeOption = {
    id: SmartBuildPurpose;
    description: string;
    statement: string;
    title: string;
};

// Dropdown rows carry a computed display label alongside the source fields.
type ClassroomItem = SmartBuildClassroom & { DisplayName: string };

type ScopeValueItem = SmartBuildScopeOption & { DisplayName: string };

export const SmartBuildStep = () => {

    const { formatMessage } = useIntl();
    const { currentUser } = useApp();

    const { editStep, loadingQuestions, productIds, sbActivityState, sbClassroomId, sbClassrooms, sbClassroomsError, sbClassroomsLoading,
        sbCustomCount, sbEligibleCount, sbMyClassroomsOnly, sbObjectives, sbPurpose, sbScopeType, sbScopeValueIds,
        sbSizeChoice, sbTopics, setProviderState, setSbScopeType, setSbScopeValueIds, setSbSizeChoice } = useCustomTestWizard();

    const [banners, setBanners] = useSuperState({
        activityDismissed: false,
        eligibilityDismissed: false,
        multiProgramDismissed: false
    });

    const classroomRef = useRef<HTMLDivElement>(null);
    const purposeRef = useRef<HTMLDivElement>(null);
    const scopeRef = useRef<HTMLDivElement>(null);
    const sizeRef = useRef<HTMLDivElement>(null);

    // Teacher-facing purposes (spec 9.3); descriptions per the design file.
    const purposes: PurposeOption[] = [
        {
            id: 'weak-skills',
            description: formatMessage({ id: 'build_a_focused_test_around_objectives_w.2f20a763', defaultMessage: 'Build a focused test around objectives where the class has enough exposure, but weaker skill performance' }),
            statement: formatMessage({ id: 'strengthen_weak_skills.9cd8f36f', defaultMessage: 'strengthen weak skills' }),
            title: formatMessage({ id: 'strengthen_weak_skills.483eb80d', defaultMessage: 'Strengthen weak skills' })
        },
        {
            id: 'readiness',
            description: formatMessage({ id: 'build_a_test_to_see_whether_the_class_ca.8f13a63b', defaultMessage: 'Build a test to see whether the class can demonstrate practiced skills in a test-like setting' }),
            statement: formatMessage({ id: 'check_readiness.f82151be', defaultMessage: 'check readiness' }),
            title: formatMessage({ id: 'check_readiness.a3cf5bfa', defaultMessage: 'Check readiness' })
        },
        {
            id: 'mastery',
            description: formatMessage({ id: 'build_a_review_test_around_objectives_th.ade424cf', defaultMessage: 'Build a review test around objectives the class previously handled well, but may need to refresh' }),
            statement: formatMessage({ id: 'maintain_mastery.8f64bd59', defaultMessage: 'maintain mastery' }),
            title: formatMessage({ id: 'maintain_mastery.25ab0513', defaultMessage: 'Maintain mastery' })
        },
        {
            id: 'coverage-gaps',
            description: formatMessage({ id: 'build_a_test_around_objectives_where_the.b4e0df70', defaultMessage: 'Build a test around objectives where the class has limited exposure or limited evidence' }),
            statement: formatMessage({ id: 'fill_coverage_gaps.b35fc5e2', defaultMessage: 'fill coverage gaps' }),
            title: formatMessage({ id: 'fill_coverage_gaps.1ddb9e12', defaultMessage: 'Fill coverage gaps' })
        },
        {
            id: 'recent-activity',
            description: formatMessage({ id: 'build_a_follow_up_test_from_the_content.37539451', defaultMessage: 'Build a follow-up test from the content this class has worked on most recently' }),
            statement: formatMessage({ id: 'review_recent_activity.47e8dd0f', defaultMessage: 'review recent activity' }),
            title: formatMessage({ id: 'review_recent_activity.57683af2', defaultMessage: 'Review recent activity' })
        },
        {
            id: 'selected-content',
            description: formatMessage({ id: 'build_a_general_test_from_the_selected_c.cffad3fc', defaultMessage: 'Build a general test from the selected content without prioritizing classroom performance data' }),
            statement: formatMessage({ id: 'review_selected_content.61c7ec70', defaultMessage: 'review selected content' }),
            title: formatMessage({ id: 'review_selected_content.d1f19130', defaultMessage: 'Review selected content' })
        }
    ];

    const multiProgram = productIds.length > 1;
    const classroomChosen = sbClassroomId > 0;
    const purposeChosen = !!sbPurpose;
    const scopeComplete = sbScopeType === 'all' || (!!sbScopeType && sbScopeValueIds.length > 0);

    const visibleClassrooms = sbMyClassroomsOnly
        ? sbClassrooms.filter(classroom => classroom.OwnerUserId === currentUser.userProfileId)
        : sbClassrooms;

    const classroomItems: ClassroomItem[] = visibleClassrooms.map(classroom => ({
        ...classroom,
        DisplayName: classroom.Instructor ? `${classroom.Name} (Instructor: ${classroom.Instructor})` : classroom.Name
    }));

    const selectedClassroom = sbClassrooms.find(classroom => classroom.Id === sbClassroomId);
    const selectedClassroomItem = classroomItems.find(item => item.Id === sbClassroomId);
    const selectedPurpose = purposes.find(purpose => purpose.id === sbPurpose);

    // Classroom drives every downstream slot, so changing it (to a genuinely
    // different classroom) resets Purpose, Scope, and Size.
    const handleClassroomChange = (classroomId: number) => {

        if (classroomId === sbClassroomId) return;

        setProviderState({
            sbActivityState: '',
            sbClassroomId: classroomId,
            sbCustomCount: 0,
            sbPurpose: '',
            sbScopeType: '',
            sbScopeValueIds: [],
            sbSizeChoice: ''
        });

    };

    const scopeOptions: SmartBuildScopeOption[] = sbScopeType === 'objective' ? sbObjectives : sbTopics;

    const scopeValueItems: ScopeValueItem[] = scopeOptions.map(option => ({
        ...option,
        DisplayName: `${option.Name} (${option.QuestionCount})`
    }));

    const selectedScopeValueItems = scopeValueItems.filter(item => sbScopeValueIds.includes(item.Id));

    // Closed-state Scope Value label: the first selected name plus a "+N" overflow
    // count; undefined defers to the dropdown's own placeholder handling.
    const scopeValueDisplayText = selectedScopeValueItems.length === 0
        ? undefined
        : selectedScopeValueItems.length === 1
            ? selectedScopeValueItems[0].Name
            : `${selectedScopeValueItems[0].Name} +${selectedScopeValueItems.length - 1}`;

    // Statement chip for Scope: count-based summary, never full value names (spec 10.6).
    const scopeChipText = !scopeComplete
        ? (formatMessage({ id: 'source.4a141a45', defaultMessage: 'source' }))
        : sbScopeType === 'all'
            ? (formatMessage({ id: 'all_available_content.2dd8d518', defaultMessage: 'all available content' }))
            : `${sbScopeValueIds.length} ${sbScopeValueIds.length === 1
                ? (sbScopeType === 'topic' ? (formatMessage({ id: 'selected_topic.ddaf58ae', defaultMessage: 'selected topic' })) : (formatMessage({ id: 'selected_objective.ac0d48e6', defaultMessage: 'selected objective' })))
                : (sbScopeType === 'topic' ? (formatMessage({ id: 'selected_topics.8d28b7f8', defaultMessage: 'selected topics' })) : (formatMessage({ id: 'selected_objectives.ff139b20', defaultMessage: 'selected objectives' })))}`;

    const resolvedCount = sbSizeChoice === 'quick' ? 10 : sbSizeChoice === 'standard' ? 25 : sbCustomCount;
    const sizeComplete = sbSizeChoice === 'quick' || sbSizeChoice === 'standard' || (sbSizeChoice === 'custom' && sbCustomCount >= 1);

    const scrollTo = (ref: typeof classroomRef) => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Tappable statement chip: filled once its slot has a value, dashed placeholder
    // until then; clicking scrolls to the slot's section (spec 6 / design file).
    const renderChip = (filled: boolean, text: string, enabled: boolean, onClick: () => void) => (
        <button
            className={'border-0 p-0 bg-transparent d-inline'}
            disabled={!enabled}
            onClick={onClick}
            type={'button'}
        >
            <span
                style={filled
                    ? { backgroundColor: 'rgba(255, 255, 255, 0.16)', border: '1px solid rgba(255, 255, 255, 0.35)' }
                    : { border: '1px dashed rgba(255, 255, 255, 0.45)' }}
                className={`d-inline-block rounded-1 px-2 py-0 fw-semibold ${filled ? 'text-white' : 'text-white-50'}`}
            >
                {text}
            </span>
        </button>
    );

    const truncate = (value: string, max = 24) => (value.length > max ? `${value.slice(0, max - 1)}…` : value);

    // Sections unlock progressively: Classroom -> Purpose -> Scope -> Size.
    const sectionClass = (enabled: boolean) => `border rounded p-3 bg-white ${enabled ? '' : 'opacity-50 pe-none'}`;

    return (
        <Row className={'g-4 py-2'}>

            {/* Statement panel — sticky so it stays visible while the slot rail scrolls. */}
            <Col lg={4} md={5} xs={12}>
                <div
                    className={'text-white rounded-3 p-4 h-100'}
                    style={{ background: PANEL_GRADIENT, minHeight: '22rem' }}
                >
                    <div className={'position-sticky'} style={{ top: '1rem' }}>

                        <div className={'d-flex align-items-center gap-2 mb-1'}>
                            <h5 className={'mb-0'}>
                                <FormattedMessage defaultMessage="Smart Build" id="smart_build.7cf76046" />
                            </h5>
                            <i className={'fa-regular fa-lightbulb'} />
                        </div>

                        <p className={'small text-white-50 mb-4'}>
                            <FormattedMessage defaultMessage="Watch your test take shape. Tap any word to change your settings" id="watch_your_test_take_shape_tap_any_word.df972a47" />
                        </p>

                        {/* One flowing sentence: chips sit inline and wrap naturally. */}
                        <p className={'fs-6 mb-0'} style={{ lineHeight: 2 }}>
                            {`${formatMessage({ id: 'for.ddf620d5', defaultMessage: 'For' })} `}
                            {renderChip(classroomChosen, classroomChosen ? truncate(selectedClassroom?.Name ?? '') : (formatMessage({ id: 'a_classroom.c7fa8e45', defaultMessage: 'a classroom' })), !multiProgram, () => scrollTo(classroomRef))}
                            {', '}
                            {`${formatMessage({ id: 'build_a_test_to.0d5d1e46', defaultMessage: 'build a test to' })} `}
                            {renderChip(purposeChosen, purposeChosen ? (selectedPurpose?.statement ?? '') : (formatMessage({ id: 'help_with.cd4e171c', defaultMessage: 'help with' })), classroomChosen, () => scrollTo(purposeRef))}
                            {` ${formatMessage({ id: 'using_content_from.1d7b0336', defaultMessage: 'using content from' })} `}
                            {renderChip(scopeComplete, scopeChipText, purposeChosen, () => scrollTo(scopeRef))}
                            {` ${formatMessage({ id: 'with.83dc6bc4', defaultMessage: 'with' })} `}
                            {renderChip(sizeComplete, sizeComplete ? String(resolvedCount) : (formatMessage({ id: 'how_many.f224acc4', defaultMessage: 'how many' })), scopeComplete, () => scrollTo(sizeRef))}
                            {` ${formatMessage({ id: 'questions.8acfb3ed', defaultMessage: 'questions.' })}`}
                        </p>

                    </div>
                </div>
            </Col>

            {/* Slot rail — the four progressive questions. */}
            <Col lg={8} md={7} xs={12}>
                <div className={'d-flex flex-column gap-3'}>

                    {/* Smart Build is single-program: with multiple programs selected the rail
                        is locked and the banner routes back or ahead (design file). */}
                    {multiProgram && !banners.multiProgramDismissed && (
                        <Alert dismissible className={'d-flex align-items-start gap-2 mb-0'} onClose={() => setBanners({ multiProgramDismissed: true })} variant={'info'}>
                            <i className={'fa-solid fa-circle-info mt-1'} />
                            <div>
                                <div className={'fw-bold'}>
                                    <FormattedMessage defaultMessage="Smart Build works with a single program" id="smart_build_works_with_a_single_program.7472f9a9" />
                                </div>
                                <div className={'small'}>
                                    <FormattedMessage defaultMessage="You selected multiple programs on the previous step. Smart Build can only generate a question set from one program at a time. You can go back and select a single program to use Smart Build, or skip ahead and select questions manually." id="you_selected_multiple_programs_on_the_pr.b3f23dfe" />
                                </div>
                                <div className={'d-flex gap-2 mt-2'}>
                                    <GMetrixButton className={'btn-sm'} onClick={() => editStep(EWizardStep.DETAILS)} variant={'outline-secondary'}>
                                        <span className={'d-inline-flex align-items-center gap-2'}>
                                            <i className={'fa-solid fa-arrow-left'} />
                                            {`${formatMessage({ id: 'previous.f825144b', defaultMessage: 'Previous' })}: ${formatMessage({ id: 'test_details.88f73a4e', defaultMessage: 'Test Details' })}`}
                                        </span>
                                    </GMetrixButton>
                                    <GMetrixButton className={'btn-sm'} onClick={() => editStep(EWizardStep.QUESTIONS)} variant={'primary'}>
                                        <span className={'d-inline-flex align-items-center gap-2'}>
                                            <FormattedMessage defaultMessage="Skip to Questions" id="skip_to_questions.f81b49b0" />
                                            <i className={'fa-solid fa-arrow-right'} />
                                        </span>
                                    </GMetrixButton>
                                </div>
                            </div>
                        </Alert>
                    )}

                    {/* Engagement-viability notice (§12): shown after the engine evaluated the
                        classroom's activity for this scope as none/low — the draft leans on
                        content structure rather than classroom performance. */}
                    {!multiProgram && (sbActivityState === 'none' || sbActivityState === 'low') && !banners.activityDismissed && (
                        <Alert dismissible className={'d-flex align-items-start gap-2 mb-0'} onClose={() => setBanners({ activityDismissed: true })} variant={'warning'}>
                            <i className={'fa-solid fa-circle-exclamation mt-1'} />
                            <div>
                                <div className={'fw-bold'}>
                                    <FormattedMessage defaultMessage="No Classroom activity for this Scope" id="no_classroom_activity_for_this_scope.d7ca4e07" />
                                </div>
                                <div className={'small'}>
                                    <FormattedMessage defaultMessage="We'll use available content, but this draft will be based on content structure rather than classroom performance. You can still generate this test." id="well_use_available_content_but_this_draf.cb6d9f37" />
                                </div>
                            </div>
                        </Alert>
                    )}

                    {!multiProgram && scopeComplete && !loadingQuestions && sbEligibleCount === 0 && !banners.eligibilityDismissed && (
                        <Alert dismissible className={'d-flex align-items-start gap-2 mb-0'} onClose={() => setBanners({ eligibilityDismissed: true })} variant={'danger'}>
                            <i className={'fa-solid fa-circle-xmark mt-1'} />
                            <div>
                                <div className={'fw-bold'}>
                                    <FormattedMessage defaultMessage="No Eligible Questions" id="no_eligible_questions.c8de56f9" />
                                </div>
                                <div className={'small'}>
                                    <FormattedMessage defaultMessage="No questions are available for the selected content. Try a broader Scope or skip Smart Build and choose questions manually." id="no_questions_are_available_for_the_selec.b8e74fec" />
                                </div>
                            </div>
                        </Alert>
                    )}

                    {/* Slot 1 — Classroom */}
                    <div className={sectionClass(!multiProgram)} ref={classroomRef}>
                        <Form.Label className={'fw-bold'}>
                            <FormattedMessage defaultMessage="Which classroom's data should this recommendation be based on?" id="which_classrooms_data_should_this_recomm.77e0a486" />
                        </Form.Label>

                        {sbClassroomsError ?
                            (
                                <div className={'d-flex align-items-center gap-2 text-danger small'}>
                                    <i className={'fa-solid fa-circle-exclamation'} />
                                    {sbClassroomsError}
                                </div>
                            )
                            :
                            (
                                <div className={'d-flex align-items-center gap-3 flex-wrap'}>
                                    <GMetrixDropdown<ClassroomItem>
                                        filterable
                                        onChange={value => {

                                            const item = Array.isArray(value) ? value[0] : value;
                                            handleClassroomChange(item?.Id ?? 0);

                                        }}
                                        className={'flex-grow-1'}
                                        dataItemKey={'Id'}
                                        dataItems={classroomItems}
                                        disabled={multiProgram || sbClassroomsLoading}
                                        placeholder={formatMessage({ id: 'select_classroom.168315b3', defaultMessage: 'Select Classroom' })}
                                        style={{ maxWidth: '26rem' }}
                                        textField={'DisplayName'}
                                        value={selectedClassroomItem}
                                    />

                                    <GMetrixCheckInput
                                        checked={sbMyClassroomsOnly}
                                        disabled={multiProgram}
                                        id={'sb-my-classrooms'}
                                        label={formatMessage({ id: 'my_classrooms.c608bc1e', defaultMessage: 'My Classrooms' })}
                                        onChange={e => setProviderState({ sbMyClassroomsOnly: e.target.checked })}
                                        type={'switch'}
                                    />
                                </div>
                            )}
                    </div>

                    {/* Slot 2 — Purpose (radio cards: visually-hidden input, whole card is the label) */}
                    <div className={sectionClass(!multiProgram && classroomChosen)} ref={purposeRef}>
                        <Form.Label className={'fw-bold'}>
                            <FormattedMessage defaultMessage="What do you want this test to help with?" id="what_do_you_want_this_test_to_help_with.bacb0d6e" />
                        </Form.Label>

                        <Row className={'g-3'}>
                            {purposes.map(purpose => {

                                const selected = sbPurpose === purpose.id;

                                return (
                                    <Col key={purpose.id} md={6} xs={12}>
                                        <input
                                            checked={selected}
                                            className={'visually-hidden'}
                                            id={`sb-purpose-${purpose.id}`}
                                            name={'sb-purpose'}
                                            onChange={() => setProviderState({ sbPurpose: purpose.id })}
                                            type={'radio'}
                                        />
                                        <label
                                            className={`d-flex gap-2 align-items-start border rounded p-3 h-100 ${selected ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                                            htmlFor={`sb-purpose-${purpose.id}`}
                                            role={'button'}
                                        >
                                            <i className={`mt-1 ${selected ? 'fa-solid fa-circle-dot text-primary' : 'fa-regular fa-circle text-secondary'}`} />
                                            <span>
                                                <span className={'fw-semibold d-block'}>
                                                    {purpose.title}
                                                </span>
                                                <span className={'small text-muted'}>
                                                    {purpose.description}
                                                </span>
                                            </span>
                                        </label>
                                    </Col>
                                );

                            })}
                        </Row>
                    </div>

                    {/* Slot 3 — Scope */}
                    <div className={sectionClass(!multiProgram && purposeChosen)} ref={scopeRef}>
                        <Form.Label className={'fw-bold'}>
                            <FormattedMessage defaultMessage="What content should this test use?" id="what_content_should_this_test_use.f15f1c43" />
                        </Form.Label>

                        {loadingQuestions ?
                            (
                                <div className={'d-flex align-items-center gap-2 text-muted small'}>
                                    <GMetrixLoader />
                                    <FormattedMessage defaultMessage="Loading available content..." id="loading_available_content.39d5cf60" />
                                </div>
                            )
                            :
                            (
                                <Row className={'g-3'}>
                                    <Col md={5} xs={12}>
                                        <Form.Select
                                            onChange={e => setSbScopeType((e.target.value || '') as SmartBuildScopeType | '')}
                                            value={sbScopeType}
                                        >
                                            {/* <option> only renders string children; a React element shows "[object Object]" */}
                                            <option value={''}>
                                                {formatMessage({ id: 'scope_type.36dc7107', defaultMessage: 'Scope Type' })}
                                            </option>
                                            <option value={'all'}>
                                                {formatMessage({ id: 'all_available_content.b7981576', defaultMessage: 'All Available Content' })}
                                            </option>
                                            <option value={'topic'}>
                                                {formatMessage({ id: 'topic.671ec7b1', defaultMessage: 'Topic' })}
                                            </option>
                                            <option value={'objective'}>
                                                {formatMessage({ id: 'objective.4eee91e9', defaultMessage: 'Objective' })}
                                            </option>
                                        </Form.Select>
                                    </Col>

                                    {(sbScopeType === 'topic' || sbScopeType === 'objective') && (
                                        <Col md={7} xs={12}>
                                            <GMetrixDropdown<ScopeValueItem>
                                                filterable
                                                multiSelect
                                                customDisplayText={scopeValueDisplayText}
                                                dataItemKey={'Id'}
                                                dataItems={scopeValueItems}
                                                onChange={value => setSbScopeValueIds(((value as ScopeValueItem[]) ?? []).map(item => item.Id))}
                                                placeholder={formatMessage({ id: 'scope_value.94f76c1e', defaultMessage: 'Scope Value' })}
                                                textField={'DisplayName'}
                                                value={selectedScopeValueItems}
                                            />
                                        </Col>
                                    )}
                                </Row>
                            )}
                    </div>

                    {/* Slot 4 — Size (presets adapt to the eligible pool; spec 11.3/11.4) */}
                    <div className={sectionClass(!multiProgram && scopeComplete && sbEligibleCount > 0)} ref={sizeRef}>
                        <Form.Label className={'fw-bold'}>
                            <FormattedMessage defaultMessage="How large should this test be?" id="how_large_should_this_test_be.fc3fdfe2" />
                        </Form.Label>

                        {/* Two-column arrangement: presets stacked left, Custom + stepper right.
                            The stepper renders persistently and only activates once Custom is
                            selected (disabled lookalike showing '--' until then). */}
                        <Row className={'g-3'}>

                            {sbEligibleCount > 10 && (
                                <Col md={5} xs={12}>
                                    <div className={'d-flex flex-column gap-2'}>
                                        <GMetrixCheckInput
                                            checked={sbSizeChoice === 'quick'}
                                            id={'sb-size-quick'}
                                            label={formatMessage({ id: 'quick_check_10_questions.c192c35a', defaultMessage: 'Quick Check (10 Questions)' })}
                                            name={'sb-size'}
                                            onChange={() => setSbSizeChoice('quick')}
                                            type={'radio'}
                                        />

                                        {sbEligibleCount > 25 && (
                                            <GMetrixCheckInput
                                                checked={sbSizeChoice === 'standard'}
                                                id={'sb-size-standard'}
                                                label={formatMessage({ id: 'standard_review_25_questions.62ea45d7', defaultMessage: 'Standard Review (25 Questions)' })}
                                                name={'sb-size'}
                                                onChange={() => setSbSizeChoice('standard')}
                                                type={'radio'}
                                            />
                                        )}
                                    </div>
                                </Col>
                            )}

                            <Col md={sbEligibleCount > 10 ? 7 : 12} xs={12}>
                                <div className={'d-flex flex-column gap-2'}>
                                    {sbEligibleCount > 10 ?
                                        (
                                            <GMetrixCheckInput
                                                checked={sbSizeChoice === 'custom'}
                                                id={'sb-size-custom'}
                                                label={formatMessage({ id: 'custom.2a5a9a35', defaultMessage: 'Custom' })}
                                                name={'sb-size'}
                                                onChange={() => setSbSizeChoice('custom')}
                                                type={'radio'}
                                            />
                                        )
                                        :
                                        (
                                            <span className={'fw-semibold'}>
                                                <FormattedMessage defaultMessage="Custom" id="custom.2a5a9a35" />
                                            </span>
                                        )}

                                    {sbSizeChoice === 'custom' || sbEligibleCount <= 10 ?
                                        (
                                            <NumberStepper
                                                ariaLabel={formatMessage({ id: 'question_count.d81a32dc', defaultMessage: 'Question count' })}
                                                max={Math.max(1, sbEligibleCount)}
                                                min={1}
                                                onChange={value => setProviderState({ sbCustomCount: value, sbSizeChoice: 'custom' })}
                                                suffix={formatMessage({ id: 'questions.04b685ee', defaultMessage: 'questions' })}
                                                value={sbCustomCount}
                                            />
                                        )
                                        :
                                        (
                                            <InputGroup className={'number-stepper flex-nowrap'} style={{ maxWidth: 260, width: 'auto' }}>
                                                <GMetrixButton disabled className={'border'} iconClass={'fa-solid fa-minus'} variant={'light'} />
                                                <Form.Control
                                                    disabled
                                                    readOnly
                                                    aria-label={formatMessage({ id: 'question_count.d81a32dc', defaultMessage: 'Question count' })}
                                                    className={'text-center border-start-0 border-end-0'}
                                                    value={'--'}
                                                />
                                                <InputGroup.Text className={'bg-white text-muted'}>
                                                    <FormattedMessage defaultMessage="questions" id="questions.04b685ee" />
                                                </InputGroup.Text>
                                                <GMetrixButton disabled className={'border'} iconClass={'fa-solid fa-plus'} variant={'light'} />
                                            </InputGroup>
                                        )}
                                </div>
                            </Col>

                        </Row>

                        {sizeComplete && resolvedCount > 60 && (
                            <div className={'d-flex align-items-start gap-2 small text-warning-emphasis mt-2'}>
                                <i className={'fa-solid fa-circle-exclamation mt-1'} />
                                <FormattedMessage defaultMessage="This test may take more than an hour to complete. Consider reducing the question count for normal classroom use." id="this_test_may_take_more_than_an_hour_to.513b8a3f" />
                            </div>
                        )}
                    </div>

                </div>
            </Col>

        </Row>
    );

};
